import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Recording, ProcessingStatus } from '../types';
import ApiService from '../services/apiService';
import { featureFlags } from '../services/featureFlags';
import { useFeature } from '../hooks/useFeature';
import { getSharedPermissionCopy } from '../utils/sharePermissions';
import RecorderLauncher from './RecorderLauncher';
import RecordingsSearch from './RecordingsSearch';
import FeaturedUpdateCard from './FeaturedUpdateCard';
import RecordingsCalendar, { toDateKey } from './RecordingsCalendar';
import RecordingsTagFilter, { TagFilterOption } from './RecordingsTagFilter';
import { RECORDER_COMPLETE_EVENT, RecorderCompleteDetail } from '../utils/recorderEvents';
import { logger } from '../utils/logger';

const PROCESSING_STATUSES = [
    ProcessingStatus.QUEUED,
    ProcessingStatus.PROCESSING,
    ProcessingStatus.NOT_STARTED,
    ProcessingStatus.UPLOADING,
];

const RecordingsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [recordings, setRecordings] = useState<Recording[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const navigate = useNavigate();
    const apiService = useMemo(() => new ApiService(), []);

    // Zoeken in opnames wordt gestuurd vanuit de frontend-settings (default- en organisatiescope).
    // De localStorage-flag blijft als lokale escape hatch voor development en tests.
    const searchFeature = useFeature('search_recordings');
    const searchFlagOverride = useMemo(() => featureFlags.isEnabled('searchRecordings'), []);
    const searchEnabled = searchFeature || searchFlagOverride;
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    // Zolang er gezocht wordt volgt de kalender de zoekresultaten i.p.v. alle opnames
    useEffect(() => {
        const shortcut = (event: KeyboardEvent) => {
            if (document.querySelector('[aria-modal="true"]')) return;
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault(); setIsSearchOpen(true);
            }
        };
        window.addEventListener('keydown', shortcut);
        return () => window.removeEventListener('keydown', shortcut);
    }, [searchEnabled]);
    const [searchResultRecordings, setSearchResultRecordings] = useState<Recording[] | null>(null);

    // Polling refs en state
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const retryCountRef = useRef(0);
    const maxRetries = 100;
    const pollingIntervalMs = 20000; // 20 seconden

    // Track welke opnames net klaar zijn met verwerken
    const prevStatusesRef = useRef<Record<string, ProcessingStatus>>({});
    const [recentlyCompleted, setRecentlyCompleted] = useState<Set<string>>(new Set());

    // Controleer of er opnames zijn die worden verwerkt
    const hasProcessingRecordings = useCallback((recordingsList: Recording[]) => {
        return recordingsList.some(recording => PROCESSING_STATUSES.includes(recording.status));
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            logger.log('Stop polling');
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            retryCountRef.current = 0;
        }
    }, []);

    const startPolling = useCallback(() => {
        logger.log('Start polling voor verwerkende opnames...');
        retryCountRef.current = 0;

        pollingIntervalRef.current = setInterval(async () => {
            retryCountRef.current += 1;
            logger.log(`Polling retry ${retryCountRef.current}/${maxRetries}`);

            try {
                const data = await apiService.getRecordings();

                // Detecteer opnames die net klaar zijn met verwerken
                const newlyCompleted = new Set<string>();
                data.forEach(rec => {
                    const prevStatus = prevStatusesRef.current[rec.id];
                    if (
                        prevStatus &&
                        prevStatus !== ProcessingStatus.PROCESSED &&
                        rec.status === ProcessingStatus.PROCESSED
                    ) {
                        newlyCompleted.add(rec.id);
                    }
                });

                // Update previous statuses
                const statusMap: Record<string, ProcessingStatus> = {};
                data.forEach(rec => { statusMap[rec.id] = rec.status; });
                prevStatusesRef.current = statusMap;

                if (newlyCompleted.size > 0) {
                    setRecentlyCompleted(newlyCompleted);
                    // Verwijder highlight na 4 seconden
                    setTimeout(() => setRecentlyCompleted(new Set()), 4000);
                }

                setRecordings(data);

                // Check of we nog steeds moeten polling
                if (!hasProcessingRecordings(data) || retryCountRef.current >= maxRetries) {
                    stopPolling();
                }
            } catch (err) {
                logger.error('Error tijdens polling:', err);

                // Stop polling bij teveel fouten
                if (retryCountRef.current >= maxRetries) {
                    stopPolling();
                }
            }
        }, pollingIntervalMs);
    }, [apiService, hasProcessingRecordings, maxRetries, pollingIntervalMs, stopPolling]);

    // Start of stop polling op basis van verwerkingsstatus
    const checkPollingStatus = useCallback((recordingsList: Recording[]) => {
        const needsPolling = hasProcessingRecordings(recordingsList);

        if (needsPolling && !pollingIntervalRef.current && retryCountRef.current < maxRetries) {
            startPolling();
        } else if (!needsPolling && pollingIntervalRef.current) {
            stopPolling();
        }
    }, [hasProcessingRecordings, maxRetries, startPolling, stopPolling]);

    const loadRecordings = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await apiService.getRecordings();
            // Bewaar initiële statussen
            const statusMap: Record<string, ProcessingStatus> = {};
            data.forEach(rec => { statusMap[rec.id] = rec.status; });
            prevStatusesRef.current = statusMap;
            setRecordings(data);
            setError(false);
            checkPollingStatus(data);
        } catch (err) {
            setError(true);
            logger.error('Error loading recordings:', err);
        } finally {
            setIsLoading(false);
        }
    }, [apiService, checkPollingStatus]);

    useEffect(() => {
        loadRecordings();
    }, [loadRecordings]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleRecordingComplete = (event: Event) => {
            const customEvent = event as CustomEvent<RecorderCompleteDetail>;
            if (customEvent.detail?.fileId) {
                loadRecordings();
            }
        };

        window.addEventListener(RECORDER_COMPLETE_EVENT, handleRecordingComplete);
        return () => {
            window.removeEventListener(RECORDER_COMPLETE_EVENT, handleRecordingComplete);
        };
    }, [loadRecordings]);

    // Cleanup bij component unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    const getStatusText = (status: ProcessingStatus) => {
        switch (status) {
            case ProcessingStatus.NOT_UPLOADED:
                return t('recordingsPage.status.notUploaded');
            case ProcessingStatus.UPLOADING:
                return t('recordingsPage.status.uploading');
            case ProcessingStatus.NOT_STARTED:
                return t('recordingsPage.status.notStarted');
            case ProcessingStatus.NOT_PROCESSED:
                return t('recordingsPage.status.notProcessed');
            case ProcessingStatus.ERROR:
                return t('recordingsPage.status.error');
            case ProcessingStatus.QUEUED:
                return t('recordingsPage.status.queued');
            case ProcessingStatus.PROCESSING:
                return t('recordingsPage.status.processing');
            case ProcessingStatus.PROCESSED:
                return t('recordingsPage.status.processed');
            default:
                return t('recordingsPage.status.unknown');
        }
    };

    const getTagDisplayName = (tagName: string) => {
        return tagName.length > 30 ? tagName.substring(0, 30) + '...' : tagName;
    };

    const recordedOnOf = (recording: Recording) => recording.recordingDateTime ?? recording.createdAt;

    // Alle tags die op minstens één opname staan, met aantal opnames per tag
    const tagOptions = useMemo<TagFilterOption[]>(() => {
        const byId = new Map<number, TagFilterOption>();
        recordings.forEach(recording => {
            recording.tags?.forEach(tag => {
                const existing = byId.get(tag.id);
                if (existing) {
                    existing.count += 1;
                } else {
                    byId.set(tag.id, { tag, count: 1 });
                }
            });
        });
        return Array.from(byId.values()).sort((a, b) => a.tag.name.localeCompare(b.tag.name));
    }, [recordings]);

    // Selectie opschonen als een tag niet meer op een opname voorkomt (bijv. na refresh)
    useEffect(() => {
        setSelectedTagIds(prev => {
            const available = new Set(tagOptions.map(option => option.tag.id));
            const pruned = prev.filter(id => available.has(id));
            return pruned.length === prev.length ? prev : pruned;
        });
    }, [tagOptions]);

    const toggleTag = useCallback((tagId: number) => {
        setSelectedTagIds(prev =>
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    }, []);

    const selectedTagObjects = useMemo(
        () => tagOptions.filter(option => selectedTagIds.includes(option.tag.id)).map(option => option.tag),
        [tagOptions, selectedTagIds]
    );

    // Tagfilter (OF-logica) — kalenderbadges en lijst volgen beide dit filter
    const tagFilteredRecordings = useMemo(() => {
        if (selectedTagIds.length === 0) return recordings;
        return recordings.filter(recording =>
            recording.tags?.some(tag => selectedTagIds.includes(tag.id))
        );
    }, [recordings, selectedTagIds]);

    // Aantal opnames per dag voor de kalender; tijdens het zoeken tellen alleen de resultaten mee
    const calendarRecordings = searchResultRecordings ?? tagFilteredRecordings;
    const countsPerDay = useMemo(() => {
        const counts: Record<string, number> = {};
        calendarRecordings.forEach(recording => {
            const date = new Date(recordedOnOf(recording));
            if (Number.isNaN(date.getTime())) return;
            const key = toDateKey(date);
            counts[key] = (counts[key] ?? 0) + 1;
        });
        return counts;
    }, [calendarRecordings]);

    // Gefilterde en per dag gegroepeerde lijst (nieuwste eerst)
    const dayGroups = useMemo(() => {
        const filtered = selectedDate
            ? tagFilteredRecordings.filter(recording => {
                const date = new Date(recordedOnOf(recording));
                return !Number.isNaN(date.getTime()) && toDateKey(date) === selectedDate;
            })
            : tagFilteredRecordings;

        const sorted = [...filtered].sort((a, b) =>
            new Date(recordedOnOf(b)).getTime() - new Date(recordedOnOf(a)).getTime()
        );

        const groups: { key: string; label: string; items: Recording[] }[] = [];
        sorted.forEach(recording => {
            const date = new Date(recordedOnOf(recording));
            const key = Number.isNaN(date.getTime()) ? 'onbekend' : toDateKey(date);
            let group = groups.find(g => g.key === key);
            if (!group) {
                let label = t('recordingsPage.unknownDateLabel');
                if (key !== 'onbekend') {
                    label = date.toLocaleDateString(i18n.language, {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                    });
                    label = label.charAt(0).toUpperCase() + label.slice(1);
                }
                group = { key, label, items: [] };
                groups.push(group);
            }
            group.items.push(recording);
        });
        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagFilteredRecordings, selectedDate, i18n.language, t]);

    const formatItemMeta = (recording: Recording) => {
        const date = new Date(recordedOnOf(recording));
        const hasDuration = Boolean(recording.duration);
        const parts: string[] = [];

        if (!Number.isNaN(date.getTime()) && !(date.getHours() === 0 && date.getMinutes() === 0)) {
            parts.push(t('recordingsPage.meta.atTime', { time: date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) }));
        }
        if (hasDuration) {
            parts.push(t('recordingsPage.meta.duration', { duration: recording.duration }));
        }
        return parts.join(' · ');
    };

    const isErrorStatus = (recording: Recording) =>
        recording.status === ProcessingStatus.ERROR || recording.status === ProcessingStatus.NOT_PROCESSED;

    // Opnames met een probleem zijn ook te openen, zodat ze via de detailpagina
    // verwijderd kunnen worden. Alleen nog-verwerkende opnames blijven dicht.
    const isOpenable = (recording: Recording) =>
        recording.status === ProcessingStatus.PROCESSED || isErrorStatus(recording);

    const handleRecordingClick = (recording: Recording) => {
        if (isOpenable(recording)) {
            navigate(`/recording/${recording.id}`, {
                state: { recording }
            });
        }
    };


    const renderStatusChip = (recording: Recording) => {
        if (recentlyCompleted.has(recording.id)) {
            return (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>
                    ✓ {t('recordingsPage.statusDone')}
                </span>
            );
        }
        if (recording.status === ProcessingStatus.PROCESSED) {
            return null;
        }
        if (PROCESSING_STATUSES.includes(recording.status)) {
            return (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}>
                    <svg className="h-3 w-3 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {getStatusText(recording.status)}
                </span>
            );
        }
        const isError = recording.status === ProcessingStatus.ERROR || recording.status === ProcessingStatus.NOT_PROCESSED;
        return (
            <span
                className="inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold"
                style={isError
                    ? { background: 'var(--danger-bg)', color: 'var(--danger)' }
                    : { background: 'var(--bg-surface-alt)', color: 'var(--text-muted)' }}
            >
                {getStatusText(recording.status)}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="flex min-h-full items-center justify-center py-32">
                <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-lg font-medium">{t('recordingsPage.loading')}</span>
                </div>
            </div>
        );
    }

    const selectedDayLabel = selectedDate
        ? (() => {
            const [y, m, d] = selectedDate.split('-').map(Number);
            const label = new Date(y, m - 1, d).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
            return label.charAt(0).toUpperCase() + label.slice(1);
        })()
        : null;

    return (
        <div className="min-h-full pb-20">
            <div className="mx-auto max-w-6xl px-5 py-9 sm:px-8 lg:px-10">
                <div className="mb-7 animate-fade-in">
                    <h1 className="font-serif text-3xl" style={{ color: 'var(--text-strong)' }}>{t('recordingsPage.title')}</h1>
                    <p className="mt-1.5" style={{ color: 'var(--text-muted)' }}>{t('recordingsPage.subtitle')}</p>
                </div>

                {error && (
                    <div
                        role="alert"
                        className="mb-6 animate-fade-in rounded-2xl border p-5 sm:p-6"
                        style={{ background: 'var(--danger-bg)', borderColor: 'rgba(216, 64, 64, 0.3)' }}
                    >
                        <div className="flex items-start gap-4">
                            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full" style={{ background: 'rgba(216, 64, 64, 0.12)' }}>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: 'var(--danger)' }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728m9.9 2.828a5 5 0 010 7.072M8.464 15.536a5 5 0 010-7.072M3 3l18 18" />
                                </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg font-semibold" style={{ color: 'var(--danger)' }}>
                                    {t('recordingsPage.connectionError.title')}
                                </h2>
                                <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--danger)', opacity: 0.85 }}>
                                    {t('recordingsPage.connectionError.body')}
                                </p>
                                <button
                                    onClick={loadRecordings}
                                    className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                                    style={{ background: 'var(--danger)' }}
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {t('recordingsPage.connectionError.retry')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <FeaturedUpdateCard />

                <RecorderLauncher />

                <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
                    {/* Lijst, per dag gegroepeerd */}
                    <div className="min-w-0 space-y-5">
                        <RecordingsSearch
                                apiService={apiService}
                                fullTextEnabled={searchEnabled}
                                fallbackRecordings={recordings}
                                isOpen={isSearchOpen}
                                onClose={() => setIsSearchOpen(false)}
                                onOpenRecording={handleRecordingClick}
                                onActiveChange={setIsSearchActive}
                                tagOptions={tagOptions}
                                selectedTagIds={selectedTagIds}
                                onToggleTag={toggleTag}
                                selectedDate={selectedDate}
                                selectedDayLabel={selectedDayLabel}
                                onClearDate={() => setSelectedDate(null)}
                                onResultsChange={setSearchResultRecordings}
                            />

                        {/* Zolang er een zoekterm staat vervangen de zoekresultaten de lijst */}
                        {!isSearchActive && (<>
                        {/* Bij een open zoekpaneel staan de actieve tagchips al in dat paneel */}
                        {!isSearchOpen && (
                            <RecordingsTagFilter
                                selectedTags={selectedTagObjects}
                                onRemoveTag={toggleTag}
                                onClear={() => setSelectedTagIds([])}
                            />
                        )}

                        {selectedDayLabel && (
                            <div className="flex items-center justify-between rounded-xl border border-primary-100 bg-container px-4 py-2.5 text-sm animate-fade-in">
                                <span style={{ color: 'var(--text)' }}>
                                    <Trans
                                        i18nKey="recordingsPage.filteredOn"
                                        values={{ day: selectedDayLabel }}
                                        components={{ strong: <strong style={{ color: 'var(--text-strong)' }} /> }}
                                    />
                                </span>
                                <button
                                    onClick={() => setSelectedDate(null)}
                                    className="font-semibold text-primary-700 hover:underline"
                                >
                                    {t('recordingsPage.showAll')}
                                </button>
                            </div>
                        )}

                        {dayGroups.length === 0 ? (
                            error ? null : (
                            <div className="animate-fade-in rounded-xl border border-primary-100 bg-container p-12 text-center">
                                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: 'var(--bg-surface-alt)' }}>
                                    <svg className="h-8 w-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-strong)' }}>
                                    {selectedDate
                                        ? t('recordingsPage.empty.titleForDay')
                                        : selectedTagIds.length > 0
                                            ? t('recordingsPage.empty.titleForTags')
                                            : t('recordingsPage.empty.title')}
                                </h3>
                                <p className="mt-2" style={{ color: 'var(--text-muted)' }}>
                                    {selectedDate
                                        ? t('recordingsPage.empty.bodyForDay')
                                        : selectedTagIds.length > 0
                                            ? t('recordingsPage.empty.bodyForTags')
                                            : t('recordingsPage.empty.body')}
                                </p>
                            </div>
                            )
                        ) : (
                            dayGroups.map((group, groupIndex) => (
                                <section
                                    key={group.key}
                                    className="animate-slide-up overflow-hidden rounded-xl border border-primary-100 bg-container"
                                    style={{ animationDelay: `${groupIndex * 0.05}s` }}
                                >
                                    <h2 className="flex items-center justify-between border-b border-primary-100 px-5 py-3.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                                        {group.label}
                                        {/* Zoekicoon in de eerste datumregel, dicht bij de content */}
                                        {groupIndex === 0 && !isSearchOpen && (
                                            <button
                                                onClick={() => setIsSearchOpen(true)}
                                                data-search-toggle
                                                aria-label={t('recordingsPage.search.open')}
                                                title={t('recordingsPage.search.open')}
                                                className="-my-1.5 -mr-2 flex h-8 w-8 flex-none items-center justify-center rounded-full transition-colors hover:bg-primary-600/[0.06]"
                                                style={{ color: 'var(--text-muted)' }}
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-4.65a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </button>
                                        )}
                                    </h2>
                                    <div className="divide-y divide-primary-100">
                                        {group.items.map(recording => {
                                            const permissionCopy = getSharedPermissionCopy(recording.sharedPermission ?? null);
                                            const isProcessed = recording.status === ProcessingStatus.PROCESSED;
                                            const openable = isOpenable(recording);
                                            const justCompleted = recentlyCompleted.has(recording.id);

                                            return (
                                                <div
                                                    key={recording.id}
                                                    onClick={() => handleRecordingClick(recording)}
                                                    className={`flex items-center justify-between gap-4 px-5 py-4 transition-colors duration-200 ${openable
                                                        ? 'cursor-pointer hover:bg-primary-600/[0.04]'
                                                        : 'cursor-default'
                                                        } ${justCompleted ? 'bg-success/5' : ''}`}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2.5">
                                                            <h3 className="truncate text-base font-medium" style={{ color: isProcessed ? 'var(--text-strong)' : 'var(--text-muted)' }}>
                                                                {recording.name || t('recordingsPage.unnamedRecording')}
                                                            </h3>
                                                            {permissionCopy && permissionCopy.label !== 'Eigen bestand' && (
                                                                <span
                                                                    className={`inline-flex flex-none items-center rounded-full border border-transparent px-2 py-0.5 text-xs font-medium ${permissionCopy.badgeClass}`}
                                                                    title={permissionCopy.description}
                                                                >
                                                                    {permissionCopy.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {formatItemMeta(recording) && (
                                                            <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                                                                {formatItemMeta(recording)}
                                                            </p>
                                                        )}

                                                        {/* Tags — klikbaar om het tagfilter te toggelen */}
                                                        {recording.tags && recording.tags.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                                {recording.tags.slice(0, 3).map((tag) => (
                                                                    <button
                                                                        key={tag.id}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            toggleTag(tag.id);
                                                                        }}
                                                                        aria-pressed={selectedTagIds.includes(tag.id)}
                                                                        title={t('recordingsPage.tagFilter.toggle', { name: tag.name })}
                                                                        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white transition-shadow hover:opacity-90"
                                                                        style={{
                                                                            backgroundColor: tag.color,
                                                                            boxShadow: selectedTagIds.includes(tag.id)
                                                                                ? `0 0 0 1.5px var(--container), 0 0 0 3px ${tag.color}`
                                                                                : undefined,
                                                                        }}
                                                                    >
                                                                        {getTagDisplayName(tag.name)}
                                                                    </button>
                                                                ))}
                                                                {recording.tags.length > 3 && (
                                                                    <span className="inline-flex items-center rounded-full border border-primary-100 px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--primary-50)', color: 'var(--text-muted)' }}>
                                                                        +{recording.tags.length - 3}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-none items-center gap-3">
                                                        {renderStatusChip(recording)}
                                                        {openable && (
                                                            <svg className="h-4 w-4 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            ))
                        )}
                        </>)}
                    </div>

                    {/* Kalender */}
                    <aside className="animate-fade-in xl:sticky xl:top-6">
                        <RecordingsCalendar
                            counts={countsPerDay}
                            selectedDate={selectedDate}
                            onSelectDate={setSelectedDate}
                        />
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default RecordingsPage;
